import React from 'react';
import { InoText } from '../InoText/InoText';

interface TimelineProgram {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  isLive?: boolean;
}

interface TimelineProps {
  programs: TimelineProgram[];
  startHour: number;
  endHour: number;
  currentTime?: string;
  isActive?: boolean;
  onProgramClick?: (program: TimelineProgram) => void;
}

export const Timeline: React.FC<TimelineProps> = ({
  programs,
  startHour,
  endHour,
  currentTime,
  isActive = false,
  onProgramClick,
}) => {
  const totalMinutes = (endHour - startHour) * 60;

  const calculatePosition = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const timeInMinutes = (hours - startHour) * 60 + minutes;
    return (timeInMinutes / totalMinutes) * 100;
  };

  const calculateWidth = (start: string, end: string) => {
    const startPos = calculatePosition(start);
    const endPos = calculatePosition(end);
    return endPos - startPos;
  };

  return (
    <div className="ino-timeline">
      {/* Time markers */}
      <div className="ino-timeline__markers">
        {Array.from({ length: endHour - startHour + 1 }).map((_, index) => (
          <div
            key={index}
            className="ino-timeline__marker"
            style={{ left: `${(index / (endHour - startHour)) * 100}%` }}
          >
            {`${(startHour + index).toString().padStart(2, '0')}:00`}
          </div>
        ))}
      </div>

      {/* Programs */}
      <div className="ino-timeline__programs">
        {programs.map((program) => (
          <div
            key={program.id}
            className={`ino-timeline__program ${
              isActive ? 'ino-timeline__program--active' : ''
            }`}
            style={{
              left: `${calculatePosition(program.startTime)}%`,
              width: `${calculateWidth(program.startTime, program.endTime)}%`,
            }}
            onClick={() => onProgramClick?.(program)}
          >
            <InoText variant="caption" color="primary">
              {program.title}
            </InoText>
            {program.isLive && (
              <span className="ino-timeline__live-badge">LIVE</span>
            )}
          </div>
        ))}

        {/* Current time indicator */}
        {currentTime && (
          <div
            className="ino-timeline__current-time"
            style={{ left: `${calculatePosition(currentTime)}%` }}
          />
        )}
      </div>
    </div>
  );
};
